import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AppLayout, authClient, LoginPage, RouteBoundary, type RouteLayout } from './shell';
import { Home } from './home';
import { modules } from './modules';

const rootRoute = createRootRoute({ component: () => <Outlet /> });

/** Public route. */
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

/**
 * Pathless layout route guarding everything below it. The session check mirrors
 * the server's deny-by-default: no valid session → bounce to /login. The server
 * is still the authority on every API call; this is UX, not the security
 * boundary.
 */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: async () => {
    const { data } = await authClient.getSession();
    if (!data?.session) {
      throw redirect({ to: '/login' });
    }
  },
  component: () => <AppLayout modules={modules} />,
});

type ModuleRouteComponent = (typeof modules)[number]['navItems'][number]['Component'];

/**
 * Apply a route's declared framing (see `RouteLayout` in shell/types.ts). AppLayout hands
 * down full height and does not scroll, so each wrapper is `h-full` and decides:
 *
 *  - `contained` — centred 80rem column, this wrapper owns the vertical scroll. The
 *    default, and what every page got unconditionally before layouts existed.
 *  - `full` — the whole content area at full height, page owns its own scrolling. The
 *    page is then responsible for not overflowing the body (the planner scrolls its grid
 *    internally and keeps its header fixed).
 */
const makeRouteComponent = (Component: ModuleRouteComponent, layout: RouteLayout = 'contained') =>
  function RouteComponent() {
    if (layout === 'full') {
      return (
        <div className="h-full">
          <Component />
        </div>
      );
    }
    return (
      <div className="h-full overflow-y-auto">
        <div className="mx-auto w-full max-w-7xl">
          <Component />
        </div>
      </div>
    );
  };

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: makeRouteComponent(Home),
});

// A module contributes a route per nav item plus any extra owned routes (the builder,
// the catalog product detail). All sit under the authed layout; the server still
// guards the underlying APIs, so an extra route needs no separate nav gating.
const moduleRoutes = modules.flatMap((m) =>
  [...m.navItems, ...(m.routes ?? [])].map((r) =>
    createRoute({
      getParentRoute: () => authedRoute,
      path: r.path,
      component: makeRouteComponent(r.Component, r.layout),
    }),
  ),
);

// A module's public pages (see `publicRoutes`): beside /login, outside the authed layout and its
// session redirect. Bare — no shell chrome — inside the same Suspense + error boundary as a
// module route, since they are lazily imported too.
const publicModuleRoutes = modules.flatMap((m) =>
  (m.publicRoutes ?? []).map((r) =>
    createRoute({
      getParentRoute: () => rootRoute,
      path: r.path,
      component: function PublicRouteComponent() {
        return (
          <RouteBoundary routeKey={r.path}>
            <r.Component />
          </RouteBoundary>
        );
      },
    }),
  ),
);

const routeTree = rootRoute.addChildren([
  loginRoute,
  ...publicModuleRoutes,
  authedRoute.addChildren([indexRoute, ...moduleRoutes]),
]);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
